import { PaginationRequestParam } from "../api";
import { LabelValuePair } from "../../../global/formFields/staticSelect/LabelValuePair";
import {
  ArbeitnehmerTyp,
  ChangesResponse,
  WorkContractStatus,
  WorkingStatus,
} from "./workContract";
import { TimetrackingRequestDto } from "./workSchedule";

export interface EmployeeWorkContractResponse {
  personalNr: string;
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string;
  accountId: string | null;
  startDate: string;
  endDate: string;
  position: string;
  imgLink: string;
  arbeitnehmerTyp: ArbeitnehmerTyp;
  workingHoursPerWeek: number;
  company: LabelValuePair;
  department: LabelValuePair;
  location: LabelValuePair;
  managedCompanies: LabelValuePair[];
  managedDepartments: LabelValuePair[];
  missingEmployeeData: boolean;
  workContractStatus: WorkContractStatus;
  working?: WorkingStatus;
  workingTimetrackingRequest?: TimetrackingRequestDto;
  timetrackingRequests: Array<TimetrackingRequestDto>;
}

export interface EmployeeDto {
  uuid: string;
  academicTitle: string | null;
  accountId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  street: string;
  postalCode: string;
  imgLink: string;
  city: string;
  sex: string;
  changes: ChangesResponse[];
  effectiveDate?: string;
  comment?: string;
  payee: string;
  paymentMethod: PaymentMethod;
  iban: string;
  bic: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
}

export enum ChurchMembership {
  NONE = "NONE",
  PROTESTANT = "PROTESTANT",
  ROMAN_CATHOLIC = "ROMAN_CATHOLIC",
  OTHER = "OTHER",
}

export enum PaymentMethod {
  BANK = "BANK",
  CASH = "CASH",
}

export enum TaxClass {
  I = "I",
  II = "II",
  III = "III",
  IV = "IV",
  IV_FACTOR = "IV_FACTOR",
  V = "V",
  VI = "VI",
}

export type TaxSocialRequest = {
  uuid: string;
  taxId: string | null;
  birthDate: string;
  privateInsured: boolean | null;
  employerContribution: boolean | null;
  beitragPkv: number | null;
  beitragPpv: number | null;
  betriebsNummerKrankenkasse: number;
  svNumberAvailable: boolean;
  svNumber: string | null;
  privatePension: boolean | null;
  changes: ChangesResponse[];
  effectiveDate?: string;
  comment?: string;
  taxIdAvailable: boolean;
  taxClass: TaxClass;
  degreeOfDisability: string;
  churchMembership: ChurchMembership;
  childBenefit: string;
  nationality: string;
};

export type TaxSocialView = {
  uuid: string;
  bic: string;
  iban: string;
  taxId: string | null;
  taxIdAvailable: boolean;
  taxClass: TaxClass;
  birthDate: string;
  privateInsured: boolean | null;
  employerContribution: boolean | null;
  beitragPkv: number | null;
  beitragPpv: number | null;
  healthInsurer: string | null;
  betriebsNummerKrankenkasse: number | null;
  svNumberAvailable: boolean;
  svNumber: string | null;
  privatePension: boolean | null;
  changes: ChangesResponse[];
  degreeOfDisability: string;
  churchMembership: ChurchMembership;
  childBenefit: number | null;
  nationality: string;
};

export enum MissingDataFilter {
  MISSING_DATA = "MISSING_DATA",
  NOT_MISSING_DATA = "NOT_MISSING_DATA",
  ANY = "ANY",
}

export enum EmployeeContractStatusFilter {
  ALL = "ALL",

  /** Only work contracts active as of today */
  ACTIVE = "ACTIVE",

  /** Former employees whose contracts have ended */
  FORMER = "FORMER",

  /** Employees whose contracts will end with-in the next 14 days */
  EXPIRING_14 = "EXPIRING_14",

  /** Employees whose contracts will end with-in the next 30 days */
  EXPIRING_30 = "EXPIRING_30",

  /** Employees whose contracts started in the past week or will start in the next week  */
  BACK_AND_FORTH_7 = "BACK_AND_FORTH_7",

  /** Employees whose contracts recently started in the past 14 days  */
  RECENTLY_ACTIVE_14 = "RECENTLY_ACTIVE_14",

  /** Employees whose contracts recently started in the past 30 days  */
  RECENTLY_ACTIVE_30 = "RECENTLY_ACTIVE_30",

  /** Employees whose contracts have not started yet */
  FUTURE = "FUTURE",

  /** Employees whose contracts will start in the next 14 days */
  FUTURE_14 = "FUTURE_14",
}

export enum ExportGroup {
  "EMPLOYEE" = "EMPLOYEE",
  "TAX_SOCIAL" = "TAX_SOCIAL",
  "WORK_CONTRACT" = "WORK_CONTRACT",
  "LOCATION" = "LOCATION",
  "DEPARTMENT" = "DEPARTMENT",
  "JOB" = "JOB",
  "SALARY" = "SALARY",
}

export enum WorkContractAdditionalDataFilter {
  NONE = "NONE",
  MANAGEMENT = "MANAGEMENT",
  TIMETRACKING = "TIMETRACKING",
  ONOFFBOARDING = "ONOFFBOARDING",
  ABSENCE = "ABSENCE",
  MISSING_FIELDS = "MISSING_FIELDS",
}

export enum AbsentStatus {
  NOT_ABSENT = "NOT_ABSENT",
  SICK = "SICK",
  VACATIONING = "VACATIONING",
  FUTURE_REQUESTS_PRESENT = "FUTURE_REQUESTS_PRESENT",
}

export interface EmployeeRequestParam extends PaginationRequestParam {
  contractStatusFilter?: EmployeeContractStatusFilter | null;
  missingEmployeeDataFilter?: MissingDataFilter | null;
  phrase: string | null;
  companyId: string | null;
  withData?: WorkContractAdditionalDataFilter;
}

export type MissingDataEmployee = {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  personalNr: string;
  employeeId: string;
  accountId: string | null;
  startDate: string | null;
  endDate: string | null;
  position: string;
  imgLink: string | null;
  arbeitnehmerTyp: ArbeitnehmerTyp;
  workingHoursPerWeek: number | null;
  company: LabelValuePair<string>;
  department: LabelValuePair<string>;
  location: LabelValuePair<string>;
  managedCompanies: LabelValuePair<string> | null;
  managedDepartments: LabelValuePair<string> | null;
  workContractStatus: WorkContractStatus;
  missingEmployeeData: boolean;
  working: WorkingStatus;
  workingTimetrackingRequest?: TimetrackingRequestDto;
  absent: AbsentStatus;
  employeeDataMissing: string[];
  payrollDataMissing: string[];
};
